import React, { useState } from 'react';
import axios from 'axios';
import './AutomationRules.css';

const API = '/api/cms';

const TRIGGERS = [
  { value: 'on_create', label: 'При создании заявки' },
  { value: 'on_status_change', label: 'При изменении статуса' },
  { value: 'on_assign', label: 'При назначении' },
  { value: 'on_overdue', label: 'При просрочке' },
  { value: 'on_priority_change', label: 'При изменении приоритета' },
  { value: 'scheduled', label: 'По расписанию' }
];

const OPERATORS = [
  { value: 'equals', label: 'Равно' },
  { value: 'not_equals', label: 'Не равно' },
  { value: 'contains', label: 'Содержит' },
  { value: 'not_contains', label: 'Не содержит' },
  { value: 'in', label: 'В списке' },
  { value: 'not_in', label: 'Не в списке' },
  { value: 'greater_than', label: 'Больше чем' },
  { value: 'less_than', label: 'Меньше чем' },
  { value: 'is_empty', label: 'Пустое' },
  { value: 'is_not_empty', label: 'Не пустое' }
];

const ACTIONS = [
  { value: 'set_priority', label: 'Установить приоритет' },
  { value: 'set_status', label: 'Установить статус' },
  { value: 'assign_to', label: 'Назначить ответственного' },
  { value: 'add_tag', label: 'Добавить тег' },
  { value: 'remove_tag', label: 'Удалить тег' },
  { value: 'set_due_date', label: 'Установить дедлайн' },
  { value: 'send_notification', label: 'Отправить уведомление' },
  { value: 'add_comment', label: 'Добавить комментарий' }
];

const FIELDS = [
  { value: 'status', label: 'Статус' },
  { value: 'priority', label: 'Приоритет' },
  { value: 'assignedTo', label: 'Ответственный' },
  { value: 'tags', label: 'Теги' },
  { value: 'service', label: 'Услуга' },
  { value: 'category', label: 'Категория' },
  { value: 'source', label: 'Источник' }
];

const AutomationRules = ({ rules, setRules, users, token, showMsg }) => {
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger: 'on_create',
    conditions: [{ field: 'status', operator: 'equals', value: '' }],
    actions: [{ type: 'set_priority', params: {} }],
    priority: 0,
    enabled: true
  });
  const [editingId, setEditingId] = useState(null);

  const h = { Authorization: `Bearer ${token}` };

  const addCondition = () => {
    setNewRule({
      ...newRule,
      conditions: [...newRule.conditions, { field: 'status', operator: 'equals', value: '' }]
    });
  };

  const removeCondition = (index) => {
    setNewRule({
      ...newRule,
      conditions: newRule.conditions.filter((_, i) => i !== index)
    });
  };

  const updateCondition = (index, field, value) => {
    const conditions = [...newRule.conditions];
    conditions[index] = { ...conditions[index], [field]: value };
    setNewRule({ ...newRule, conditions });
  };

  const addAction = () => {
    setNewRule({
      ...newRule,
      actions: [...newRule.actions, { type: 'set_priority', params: {} }]
    });
  };

  const removeAction = (index) => {
    setNewRule({
      ...newRule,
      actions: newRule.actions.filter((_, i) => i !== index)
    });
  };

  const updateAction = (index, field, value) => {
    const actions = [...newRule.actions];
    actions[index] = { ...actions[index], [field]: value };
    setNewRule({ ...newRule, actions });
  };

  const createRule = async () => {
    if (!newRule.name.trim()) {
      showMsg('⚠️ Укажите имя правила');
      return;
    }
    try {
      const res = await axios.post(`${API}/automation/rules`, newRule, { headers: h });
      setRules([...rules, res.data]);
      setNewRule({
        name: '',
        description: '',
        trigger: 'on_create',
        conditions: [{ field: 'status', operator: 'equals', value: '' }],
        actions: [{ type: 'set_priority', params: {} }],
        priority: 0,
        enabled: true
      });
      showMsg('✅ Правило создано');
    } catch (e) {
      showMsg(e.response?.data?.message || 'Ошибка создания правила');
    }
  };

  const toggleRule = async (id, enabled) => {
    try {
      await axios.put(`${API}/automation/rules/${id}`, { enabled: !enabled }, { headers: h });
      setRules(rules.map(r => r._id === id ? { ...r, enabled: !enabled } : r));
    } catch (e) {
      showMsg('Ошибка обновления');
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Удалить правило?')) return;
    try {
      await axios.delete(`${API}/automation/rules/${id}`, { headers: h });
      setRules(rules.filter(r => r._id !== id));
      showMsg('✅ Правило удалено');
    } catch (e) {
      showMsg('Ошибка удаления');
    }
  };

  const getActionParams = (action) => {
    switch (action.type) {
      case 'set_priority':
        return (
          <select 
            value={action.params?.value || 'medium'} 
            onChange={e => updateAction(
              newRule.actions.indexOf(action), 
              'params', 
              { ...action.params, value: e.target.value }
            )}
          >
            <option value="low">Низкий</option>
            <option value="medium">Средний</option>
            <option value="high">Высокий</option>
            <option value="critical">Критический</option>
          </select>
        );
      case 'set_status':
        return (
          <select 
            value={action.params?.value || 'new'} 
            onChange={e => updateAction(
              newRule.actions.indexOf(action), 
              'params', 
              { ...action.params, value: e.target.value }
            )}
          >
            <option value="new">Новая</option>
            <option value="in_progress">В работе</option>
            <option value="completed">Завершена</option>
            <option value="cancelled">Отменена</option>
          </select>
        );
      case 'assign_to':
        return (
          <select 
            value={action.params?.userId || ''} 
            onChange={e => updateAction(
              newRule.actions.indexOf(action), 
              'params', 
              { ...action.params, userId: e.target.value }
            )}
          >
            <option value="">Выберите пользователя</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </select>
        );
      case 'add_tag':
      case 'remove_tag':
        return (
          <input 
            type="text" 
            placeholder="Имя тега" 
            value={action.params?.tagName || ''} 
            onChange={e => updateAction(
              newRule.actions.indexOf(action), 
              'params', 
              { ...action.params, tagName: e.target.value }
            )}
          />
        );
      case 'add_comment':
        return (
          <input 
            type="text" 
            placeholder="Текст комментария" 
            value={action.params?.text || ''} 
            onChange={e => updateAction(
              newRule.actions.indexOf(action), 
              'params', 
              { ...action.params, text: e.target.value }
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="automation-rules">
      <h2>🤖 Правила автоматизации</h2>
      
      <div className="create-rule-form">
        <h3>Создать правило</h3>
        <div className="form-section">
          <div className="form-group">
            <label>Название</label>
            <input 
              type="text" 
              value={newRule.name} 
              onChange={e => setNewRule({ ...newRule, name: e.target.value })} 
              placeholder="Например: Установить высокий приоритет для критичных"
            />
          </div>
          <div className="form-group">
            <label>Описание</label>
            <input 
              type="text" 
              value={newRule.description} 
              onChange={e => setNewRule({ ...newRule, description: e.target.value })} 
              placeholder="Описание правила"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Триггер</label>
              <select value={newRule.trigger} onChange={e => setNewRule({ ...newRule, trigger: e.target.value })}>
                {TRIGGERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Приоритет</label>
              <input 
                type="number" 
                min="0" 
                max="100" 
                value={newRule.priority} 
                onChange={e => setNewRule({ ...newRule, priority: parseInt(e.target.value) })} 
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Условия</h4>
          {newRule.conditions.map((condition, index) => (
            <div key={index} className="condition-row">
              <select value={condition.field} onChange={e => updateCondition(index, 'field', e.target.value)}>
                {FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <select value={condition.operator} onChange={e => updateCondition(index, 'operator', e.target.value)}>
                {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                <input 
                  type="text" 
                  value={condition.value} 
                  onChange={e => updateCondition(index, 'value', e.target.value)} 
                  placeholder="Значение"
                />
              )}
              {newRule.conditions.length > 1 && (
                <button className="btn-sm btn-danger" onClick={() => removeCondition(index)}>✕</button>
              )}
            </div>
          ))}
          <button className="btn btn-outline btn-sm" onClick={addCondition}>+ Добавить условие</button>
        </div>

        <div className="form-section">
          <h4>Действия</h4>
          {newRule.actions.map((action, index) => (
            <div key={index} className="action-row">
              <select value={action.type} onChange={e => updateAction(index, 'type', e.target.value)}>
                {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
              {getActionParams(action)}
              {newRule.actions.length > 1 && (
                <button className="btn-sm btn-danger" onClick={() => removeAction(index)}>✕</button>
              )}
            </div>
          ))}
          <button className="btn btn-outline btn-sm" onClick={addAction}>+ Добавить действие</button>
        </div>

        <button className="btn btn-primary" onClick={createRule}>➕ Создать правило</button>
      </div>

      <div className="rules-list">
        <h3>Существующие правила ({rules.length})</h3>
        {rules.length === 0 ? (
          <p className="empty-state">Нет правил автоматизации</p>
        ) : (
          <div className="rules-grid">
            {rules.map(rule => (
              <div key={rule._id} className={`rule-card ${rule.enabled ? '' : 'disabled'}`}>
                <div className="rule-header">
                  <div>
                    <h4>{rule.name}</h4>
                    <span className="trigger-badge">{TRIGGERS.find(t => t.value === rule.trigger)?.label}</span>
                  </div>
                  <label className="toggle">
                    <input 
                      type="checkbox" 
                      checked={rule.enabled} 
                      onChange={() => toggleRule(rule._id, rule.enabled)} 
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <p className="rule-description">{rule.description}</p>
                <div className="rule-conditions">
                  <strong>Условия:</strong>
                  {rule.conditions.map((c, i) => (
                    <span key={i} className="condition-tag">
                      {FIELDS.find(f => f.value === c.field)?.label} {OPERATORS.find(o => o.value === c.operator)?.label} {c.value}
                    </span>
                  ))}
                </div>
                <div className="rule-actions">
                  <strong>Действия:</strong>
                  {rule.actions.map((a, i) => (
                    <span key={i} className="action-tag">
                      {ACTIONS.find(act => act.value === a.type)?.label}
                    </span>
                  ))}
                </div>
                <div className="rule-footer">
                  <span>Выполнено: {rule.executionCount} раз</span>
                  <button className="btn-sm btn-danger" onClick={() => deleteRule(rule._id)}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationRules;
